/**
 * Feed-account helpers for ledger transactions.
 *
 * The type-name constants and the two predicates (`isFeedAccountType`,
 * `isCreditCardType`) now live in `@releve/shared` so the backend can share them —
 * it needs `isFeedAccountType` for the bank-connection link guard. They are
 * re-exported here so the existing `@/lib/bankAccount` import path keeps working
 * unchanged across the app.
 *
 * `transactionTouchesFeedAccount` stays here: it is shaped by the ledger-entry
 * query embeds used by `useLedgerTransactions` and the AP/AR hooks, and has no
 * backend caller.
 */
export {
  BANK_ACCOUNT_TYPE_NAME,
  CREDIT_CARD_ACCOUNT_TYPE_NAME,
  FEED_ACCOUNT_TYPE_NAMES,
  isFeedAccountType,
  isCreditCardType,
} from '@releve/shared';

import { isFeedAccountType } from '@releve/shared';

/**
 * The two account embed shapes used across the ledger-entry queries:
 * `useLedgerTransactions` aliases the account as `account`, while the
 * AP/AR hooks embed it as `chart_of_accounts`. Either may carry the
 * `account_type` join.
 */
interface EntryWithAccountType {
  account?: { account_type?: { type_name?: string | null } | null } | null;
  chart_of_accounts?: { account_type?: { type_name?: string | null } | null } | null;
}

/**
 * True when any of a transaction's ledger entries posts against a feed account
 * (Bank or Credit Cards) — i.e. the transaction clears through a feed account
 * and is therefore eligible for bank-feed matching.
 */
export function transactionTouchesFeedAccount(
  entries: EntryWithAccountType[] | null | undefined,
): boolean {
  if (!entries?.length) return false;
  return entries.some((entry) => {
    const typeName =
      entry.account?.account_type?.type_name ??
      entry.chart_of_accounts?.account_type?.type_name;
    return isFeedAccountType(typeName);
  });
}

/**
 * @deprecated Use {@link transactionTouchesFeedAccount}. Retained as an alias so
 * existing call sites keep working; feed eligibility now includes Credit Cards.
 */
export const transactionTouchesBank = transactionTouchesFeedAccount;
